import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { PredictionTeamStats } from "@/hooks/usePredictions";

interface PredictionStatsTableProps {
  homeStats: PredictionTeamStats | null;
  awayStats: PredictionTeamStats | null;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
}

interface StatRow {
  label: string;
  values: [string, string, string]; // [Home, Away, Overall]
}

const formatAverage = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? "-" : num.toFixed(2);
};

const formatPercentage = (value: number | null | undefined, total: number | null | undefined): string => {
  if (!value || !total || total === 0) return "0%";
  return `${((value / total) * 100).toFixed(0)}%`;
};

const calculateSeasonStats = (stats: PredictionTeamStats | null): StatRow[] => {
  if (!stats) return [];

  const totalHome = stats.league_fixtures_played_home || 0;
  const totalAway = stats.league_fixtures_played_away || 0;
  const totalGlobal = totalHome + totalAway;

  return [
    {
      label: "Average goals scored per game",
      values: [
        formatAverage(totalHome > 0 ? (stats.league_goals_for_total_home || 0) / totalHome : 0),
        formatAverage(totalAway > 0 ? (stats.league_goals_for_total_away || 0) / totalAway : 0),
        formatAverage(
          totalGlobal > 0
            ? ((stats.league_goals_for_total_home || 0) + (stats.league_goals_for_total_away || 0)) / totalGlobal
            : 0
        ),
      ],
    },
    {
      label: "Average goals conceded per game",
      values: [
        formatAverage(totalHome > 0 ? (stats.league_goals_against_total_home || 0) / totalHome : 0),
        formatAverage(totalAway > 0 ? (stats.league_goals_against_total_away || 0) / totalAway : 0),
        formatAverage(
          totalGlobal > 0
            ? ((stats.league_goals_against_total_home || 0) + (stats.league_goals_against_total_away || 0)) / totalGlobal
            : 0
        ),
      ],
    },
    {
      label: "Average goals scored + conceded",
      values: [
        formatAverage(
          totalHome > 0
            ? ((stats.league_goals_for_total_home || 0) + (stats.league_goals_against_total_home || 0)) / totalHome
            : 0
        ),
        formatAverage(
          totalAway > 0
            ? ((stats.league_goals_for_total_away || 0) + (stats.league_goals_against_total_away || 0)) / totalAway
            : 0
        ),
        formatAverage(
          totalGlobal > 0
            ? ((stats.league_goals_for_total_home || 0) +
                (stats.league_goals_for_total_away || 0) +
                (stats.league_goals_against_total_home || 0) +
                (stats.league_goals_against_total_away || 0)) /
                totalGlobal
            : 0
        ),
      ],
    },
    {
      label: "Clean sheets (%)",
      values: [
        formatPercentage(stats.clean_sheet_home, totalHome),
        formatPercentage(stats.clean_sheet_away, totalAway),
        formatPercentage(stats.clean_sheet_total, totalGlobal),
      ],
    },
    {
      label: "Failed to score (%)",
      values: [
        formatPercentage(stats.failed_to_score_home, totalHome),
        formatPercentage(stats.failed_to_score_away, totalAway),
        formatPercentage((stats.failed_to_score_home || 0) + (stats.failed_to_score_away || 0), totalGlobal),
      ],
    },
  ];
};

interface Last5StatRow {
  label: string;
  value: string;
}

const calculateLast5Stats = (stats: PredictionTeamStats | null): Last5StatRow[] => {
  if (!stats) return [];

  const goalsForAvg = parseFloat(stats.last_5_goals_for_average || "0");
  const goalsAgainstAvg = parseFloat(stats.last_5_goals_against_average || "0");

  return [
    {
      label: "Average goals scored per game",
      value: formatAverage(goalsForAvg),
    },
    {
      label: "Average goals conceded per game",
      value: formatAverage(goalsAgainstAvg),
    },
    {
      label: "Average goals scored + conceded",
      value: formatAverage(goalsForAvg + goalsAgainstAvg),
    },
  ];
};

export function PredictionStatsTable({
  homeStats,
  awayStats,
  homeTeamName,
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo,
}: PredictionStatsTableProps) {
  const renderStatsTable = (homeRows: StatRow[], awayRows: StatRow[]) => (
    <div className="overflow-x-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Home Team */}
        <Card className="sketch-border bg-blue-50 dark:bg-blue-950/20 overflow-hidden">
            <CardContent className="p-4 md:p-5 flex flex-col justify-center min-h-[300px]">
            {/* Home Header */}
            <div className="flex items-center justify-center gap-2 pb-3 mb-4 border-b border-blue-200 dark:border-blue-800">
              {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-6 h-6" />}
              <span className="font-black text-sm">{homeTeamName}</span>
            </div>

            {/* Sub-headers */}
            <div className="grid grid-cols-4 gap-1.5 mb-2 text-xs font-bold text-center">
              <div></div>
              <div className="text-blue-700 dark:text-blue-300">Home</div>
              <div className="text-blue-700 dark:text-blue-300">Away</div>
              <div className="text-blue-700 dark:text-blue-300">Overall</div>
            </div>

            {/* Rows */}
            {homeRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-4 gap-1.5 py-3 border-t border-blue-100 dark:border-blue-900 text-xs"
              >
                <div className="font-medium text-left">{row.label}</div>
                <div className="text-center font-black text-sm bg-blue-100 dark:bg-blue-900/30 rounded px-1">{row.values[0]}</div>
                <div className="text-center font-semibold">{row.values[1]}</div>
                <div className="text-center font-semibold">{row.values[2]}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Away Team */}
        <Card className="sketch-border bg-orange-50 dark:bg-orange-950/20 overflow-hidden">
            <CardContent className="p-4 md:p-5 flex flex-col justify-center min-h-[300px]">
            {/* Away Header */}
            <div className="flex items-center justify-center gap-2 pb-3 mb-4 border-b border-orange-200 dark:border-orange-800">
              {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-6 h-6" />}
              <span className="font-black text-sm">{awayTeamName}</span>
            </div>

            {/* Sub-headers */}
            <div className="grid grid-cols-4 gap-1.5 mb-2 text-xs font-bold text-center">
              <div></div>
              <div className="text-orange-700 dark:text-orange-300">Home</div>
              <div className="text-orange-700 dark:text-orange-300">Away</div>
              <div className="text-orange-700 dark:text-orange-300">Overall</div>
            </div>

            {/* Rows */}
            {awayRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-4 gap-1.5 py-3 border-t border-orange-100 dark:border-orange-900 text-xs"
              >
                <div className="font-medium text-left">{row.label}</div>
                <div className="text-center font-semibold">{row.values[0]}</div>
                <div className="text-center font-black text-sm bg-orange-100 dark:bg-orange-900/30 rounded px-1">{row.values[1]}</div>
                <div className="text-center font-semibold">{row.values[2]}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderLast5StatsTable = (homeRows: Last5StatRow[], awayRows: Last5StatRow[]) => (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Home Team */}
        <Card className="sketch-border bg-blue-50 dark:bg-blue-950/20 overflow-hidden">
          <CardContent className="p-1.5 md:p-2">
            {/* Home Header */}
            <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b border-blue-200 dark:border-blue-800">
              {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-6 h-6" />}
              <span className="font-black text-sm">{homeTeamName}</span>
            </div>

            {/* Sub-header */}
            <div className="grid grid-cols-2 gap-1.5 mb-2 text-xs font-bold">
              <div></div>
              <div className="text-center text-blue-700 dark:text-blue-300">Overall</div>
            </div>

            {/* Rows */}
            {homeRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 gap-1.5 py-2 border-t border-blue-100 dark:border-blue-900 text-xs"
              >
                <div className="font-medium text-left">{row.label}</div>
                <div className="text-center font-black text-lg bg-blue-100 dark:bg-blue-900/30 rounded px-2 py-0.5">{row.value}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Away Team */}
        <Card className="sketch-border bg-orange-50 dark:bg-orange-950/20 overflow-hidden">
          <CardContent className="p-1.5 md:p-2">
            {/* Away Header */}
            <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b border-orange-200 dark:border-orange-800">
              {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-6 h-6" />}
              <span className="font-black text-sm">{awayTeamName}</span>
            </div>

            {/* Sub-header */}
            <div className="grid grid-cols-2 gap-1.5 mb-2 text-xs font-bold">
              <div></div>
              <div className="text-center text-orange-700 dark:text-orange-300">Overall</div>
            </div>

            {/* Rows */}
            {awayRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 gap-1.5 py-2 border-t border-orange-100 dark:border-orange-900 text-xs"
              >
                <div className="font-medium text-left">{row.label}</div>
                <div className="text-center font-black text-lg bg-orange-100 dark:bg-orange-900/30 rounded px-2 py-0.5">{row.value}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <Tabs defaultValue="season" className="w-full">
      <TabsList className="w-full justify-start mb-4">
        <TabsTrigger value="season">TOTAL SEASON</TabsTrigger>
        <TabsTrigger value="last5">LAST 5 GAMES</TabsTrigger>
      </TabsList>

      <TabsContent value="season" className="mt-0">
        {renderStatsTable(calculateSeasonStats(homeStats), calculateSeasonStats(awayStats))}
      </TabsContent>

      <TabsContent value="last5" className="mt-0">
        {renderLast5StatsTable(calculateLast5Stats(homeStats), calculateLast5Stats(awayStats))}
      </TabsContent>
    </Tabs>
  );
}
